import { Link, Navigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Building2, Users as UsersIcon, ArrowRight, Mail, ServerCog, ShieldCheck, CalendarClock, ClipboardCheck, Palette } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { APP_VERSION, APP_CODENAME } from "@/version";

const CARDS = [
  {
    to: "/settings/company",
    label: "Company Profile",
    description: "Branding, address and business details used on reports and emails.",
    icon: Building2,
    permission: "settings.read",
    testId: "settings-card-company",
  },
  {
    to: "/settings/contact-roles",
    label: "Contact Roles",
    description: "Manage the roles that can be assigned to customer contacts.",
    icon: UsersIcon,
    permission: "settings.contact_roles.read",
    testId: "settings-card-contact-roles",
  },
  {
    to: "/settings/roles",
    label: "Roles & Permissions",
    description: "Define user roles and grant permissions across the app.",
    icon: ShieldCheck,
    permission: "settings.roles.read",
    testId: "settings-card-roles",
  },
  {
    to: "/settings/scheduling",
    label: "Scheduling Defaults",
    description: "Default frequency + anchor offset used when creating a service schedule.",
    icon: CalendarClock,
    permission: "settings.scheduling.read",
    testId: "settings-card-scheduling",
  },
  {
    to: "/settings/checklist-defaults",
    label: "Checklist Defaults",
    description: "Global rules that shape the engineer's on-tablet checklist and photo behaviour.",
    icon: ClipboardCheck,
    permission: "settings.checklist.read",
    testId: "settings-card-checklist-defaults",
  },
  {
    to: "/settings/security",
    label: "Security events",
    description: "Audit trail of privilege-escalation attempts and other security-sensitive operations. Super admin only.",
    icon: ShieldCheck,
    permission: "settings.security.read",
    testId: "settings-card-security",
  },
  {
    to: "/settings/branding",
    label: "Branding",
    description: "Runtime overrides for primary color, accent color, and font family. Applied at every session boot.",
    icon: Palette,
    permission: "settings.branding.write",
    testId: "settings-card-branding",
  },
  {
    to: "/settings/templates",
    label: "Email & PDF Templates",
    description: "Edit the wording of outgoing report emails and PDF report footers.",
    icon: Mail,
    permission: "settings.templates.read",
    testId: "settings-card-templates",
  },
  {
    to: "/settings/smtp",
    label: "SMTP",
    description: "Outgoing email configuration status and test-send tool.",
    icon: ServerCog,
    permission: "settings.smtp.read",
    testId: "settings-card-smtp",
  },
];

export default function Settings() {
  const { user, hasPermission } = useAuth();
  if (user?.role === "engineer") return <Navigate to="/my/jobs" replace />;

  const cards = CARDS.filter((c) => hasPermission(c.permission));

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <PageHeader
        testId="settings-header"
        title="Settings"
        subtitle="Application configuration, branding, and directory-level settings."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="settings-cards-grid">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Link key={c.to} to={c.to} data-testid={c.testId} className="group">
              <Card className="rounded-md h-full hover:border-primary transition-colors">
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="h-8 w-8 rounded-sm bg-primary/10 text-primary flex items-center justify-center">
                      <Icon className="h-4 w-4" />
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <div className="text-sm font-medium">{c.label}</div>
                  <div className="text-xs text-muted-foreground leading-relaxed">{c.description}</div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div
        className="pt-6 text-center text-[11px] text-muted-foreground/80 tracking-wide border-t border-border"
        data-testid="app-version-display-settings"
      >
        Field Service · v{APP_VERSION} · {APP_CODENAME}
      </div>
    </div>
  );
}
